import { IconArrowLeft } from '@tabler/icons-react'

import Text from 'components/ui/text'
import { NOT_TRIAL_PLANS } from 'utils/consts.mjs'
import { translate } from 'utils/translation'
import { getCurrencyAndRegion, getSubscriptionOptions } from 'utils/payment'
import { isLifetimeValid, localizedMoment } from 'utils/functions'
import { formatNumber } from 'utils/numbers'
import { useUser } from 'hooks/use-user'
import Button from 'components/ui/button'

import { TIER_TITLES } from '../consts'

import { SubscribingPolicy } from './subscribing-policy'

interface NewCheckoutTimeProps {
  isFreeTrialUsed: boolean
  tier: string
  interval: string
  onBack: () => void
  promoFeature?: string
  isMobile: boolean
  setShowMobilePaymentForm: (show: boolean) => void
}

interface SubscriptionOption {
  planLabel: string
  discountPrice?: number
  unitPrice: number
}

const PLANS_CONFIGURATION: Record<
  string,
  { paymentInterval: string; benefits: string[] }
> = {
  monthly: {
    paymentInterval: 'billed_monthly',
    benefits: ['cancel_without_fees'],
  },
  lifetime: {
    paymentInterval: 'one_time_pay',
    benefits: ['lifetime_activates_today'],
  },
}

const DIVIDER_CLASSES = 'tw-my-4 xs:tw-my-6 tw-h-px tw-bg-zinc-200'

export const NewCheckoutTimeLine = ({
  isFreeTrialUsed,
  tier,
  interval,
  onBack,
  promoFeature,
  isMobile,
  setShowMobilePaymentForm,
}: NewCheckoutTimeProps) => {
  const user = useUser()
  const isLifetimeLeft = isLifetimeValid()

  const subscriptionOptions = getSubscriptionOptions({
    countryCode: user.countryCode,
    tier,
    _isLifetimeValid: isLifetimeLeft,
    promoFeature,
  }) as SubscriptionOption[]

  const currentSubscription = subscriptionOptions.find(
    (subscription) => subscription.planLabel === interval,
  )

  const duePrice =
    currentSubscription?.discountPrice || currentSubscription?.unitPrice || 0
  const { region } = getCurrencyAndRegion(user.countryCode)

  const isTrial = !isFreeTrialUsed && !NOT_TRIAL_PLANS.includes(interval)

  const getTotalText = () => {
    if (interval === 'lifetime') {
      return 'total_pay'
    }
    if (!isTrial) return 'total_today'
    return 'total_after_trial'
  }

  const renderBenefits = (str = '') => {
    return (
      <>
        <li key="start-date">
          <Text
            className="tw-m-0 tw-p-0 tw-text-xs tw-text-zinc-500 xs:tw-text-sm"
            renderChildren
          >
            {!!str && `${str}. `}
            {interval !== 'lifetime' &&
              translate(
                'billing_starts',
                localizedMoment()
                  .add(!isTrial ? 0 : 7, 'day')
                  .format('LL'),
              )}
          </Text>
        </li>
        {PLANS_CONFIGURATION?.[
          currentSubscription?.planLabel || ''
        ]?.benefits?.map((benefit, index) => (
          <li key={`${benefit}-${index}`} className="tw-mt-1">
            <Text className="tw-m-0 tw-p-0 tw-text-xs tw-text-zinc-500 xs:tw-text-sm">
              {translate(benefit)}
            </Text>
          </li>
        ))}
      </>
    )
  }

  const planText = translate(TIER_TITLES?.[tier as keyof typeof TIER_TITLES])
  const intervalText = translate(
    interval === 'lifetime' ? 'lifetime_license' : interval,
  )
  const subscriptionText =
    interval === 'lifetime'
      ? `${planText} - ${intervalText}`
      : translate('checkout_subscribe_to', planText, intervalText)

  const subscriptionIntervalText = translate(
    PLANS_CONFIGURATION?.[currentSubscription?.planLabel || '']
      ?.paymentInterval || '',
  )

  return (
    <div className="tw-flex tw-w-full tw-flex-grow tw-flex-col">
      <div className="tw-flex tw-grow tw-flex-col">
        <div className="tw-flex tw-items-center tw-gap-3">
          <IconArrowLeft
            width={28}
            height={28}
            className="tw-flex tw-cursor-pointer tw-rounded-md tw-border tw-border-solid tw-border-zinc-300 tw-p-1 tw-text-zinc-950"
            onClick={onBack}
          />
          <Text className="tw-m-0 tw-p-0 tw-text-2xl tw-font-medium tw-text-zinc-950">
            checkout_summary
          </Text>
        </div>

        <div className={DIVIDER_CLASSES} />

        <Text
          className="tw-m-0 tw-p-0 tw-text-sm tw-font-bold xs:tw-text-base"
          renderChildren
        >
          {subscriptionText}
        </Text>
        <Text className="tw-m-0 tw-mt-1 tw-p-0 tw-text-2xl tw-font-bold xs:tw-text-4xl">
          {duePrice}
        </Text>

        <div className={DIVIDER_CLASSES} />

        <div className="tw-flex tw-justify-between">
          <div className="tw-flex-grow">
            <Text className="tw-m-0 tw-p-0 tw-text-sm tw-font-medium xs:tw-text-base">
              {getTotalText()}
            </Text>
            {!isTrial ? (
              <ul className="tw-m-0 tw-p-0">
                {renderBenefits(subscriptionIntervalText)}
              </ul>
            ) : (
              <Text className="tw-m-0 tw-mt-1 tw-p-0 tw-text-xs tw-text-zinc-500 xs:tw-text-sm">
                {subscriptionIntervalText}
              </Text>
            )}
          </div>
          <Text
            as="span"
            renderChildren
            className="tw-m-0 tw-p-0 tw-text-sm tw-font-medium xs:tw-text-base"
          >
            {duePrice}
          </Text>
        </div>

        {isTrial && (
          <>
            <div className={DIVIDER_CLASSES} />

            <div className="tw-mt-0.5 tw-flex tw-justify-between">
              <div className="tw-flex-grow">
                <Text
                  className="tw-m-0 tw-flex tw-items-center tw-gap-1 tw-p-0 tw-text-sm tw-font-medium xs:tw-text-base"
                  renderChildren
                >
                  {translate('total_today')}
                  <Text
                    as="span"
                    className="tw-m-0 tw-rounded-full tw-border tw-border-dashed tw-border-orange-500 tw-px-2.5 tw-py-px tw-text-[10px] tw-uppercase tw-text-orange-500"
                  >
                    seven_days_free
                  </Text>
                </Text>

                <ul className="tw-m-0 tw-mt-1 tw-p-0">{renderBenefits()}</ul>
              </div>
              <Text
                as="span"
                className="tw-m-0 tw-p-0 tw-text-sm tw-font-medium xs:tw-text-base"
              >
                {/* @ts-ignore TODO: Wait for TS component update */}
                {formatNumber(0, true, 0, region, false)}
              </Text>
            </div>
          </>
        )}
      </div>
      <div className="tw-flex">
        {isMobile ? (
          <Button
            variant="legacy-primary"
            className="tw-mx-auto tw-mt-4 tw-h-10 tw-w-full tw-bg-orange-500 !tw-p-0 !tw-text-base tw-font-normal tw-normal-case"
            onClick={() => setShowMobilePaymentForm(true)}
          >
            {translate('continue_checkout')}
          </Button>
        ) : (
          <SubscribingPolicy planText={planText} />
        )}
      </div>
    </div>
  )
}
